import * as React from 'react'
import { FC, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useAppDispatch } from '@user/store/hooks'
import maplibregl from 'maplibre-gl'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Link from '@mui/material/Link'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import Typography from '@mui/material/Typography'
import CloseIcon from '@mui/icons-material/Close'
import {
  fetchTerritoryBoundaries,
  fetchTerritoryRuleUsage,
  fetchTerritorySnapshot,
} from '@user/store/territory/api'
import {
  selectTerritoryBoundariesById,
  selectTerritorySnapshotErrorIds,
  selectTerritorySnapshotLoadingIds,
  selectTerritorySnapshotsById,
} from '@user/store/territory/slice'
import { Territory, TerritoryRuleUsage } from '@user/store/territory/typings'
import { useMapLibreTooltipPosition } from '@user/components/HereMap/MapPin/components/MapPinTooltip/hooks/useMapLibreTooltipPosition'
import { useMeasuredTooltipHeight } from '@user/components/HereMap/MapPin/components/MapPinTooltip/hooks/useMeasuredTooltipHeight'
import { DEFAULT_TERRITORY_COLOR } from '@user/store/territory/constants'
import { METRICS, ownerCountLabel } from './territoryMetrics'

const BUBBLE_WIDTH = 300
/** Roughly the loaded height, used only until the real one is measured. */
const BUBBLE_FALLBACK_HEIGHT = 220

type Props = {
  map: maplibregl.Map
  territory: Territory
  /** Where the click landed. The bubble is anchored there and travels with the map. */
  lngLat: { lng: number; lat: number }
  /** Other territories the same click fell inside, in draw order. Usually empty. */
  alsoIn: Territory[]
  onClose: () => void
  /** Absent for a rep: the full report is reached from the admin-only manage panel. */
  onViewReport?: () => void
}

/**
 * What one territory contains, on click (WWM-17508).
 *
 * Hover is free and says only which territory this is; the numbers cost a request, so they are
 * fetched here and nowhere else. Sweeping across a map of eight territories would otherwise
 * fire eight live aggregate queries, each of which counts records inside a polygon.
 *
 * The figures are the same six the draft preview shows, from the same endpoint and the same
 * twelve-month window, so "what this area contains" means one thing whether the shape has been
 * saved or not. Owner and team names ride on that response too -- no second lookup, and no
 * dependency on the sales-team store, which the map does not otherwise load.
 *
 * Positioned with the pin bubble's own placement hook, so it flips and clamps at the container
 * edges exactly as a pin bubble does rather than inventing a second set of rules.
 *
 * **Who gets leads here can come from two places**, and the bubble says which. A territory drawn
 * from an assignment rule carries no owners of its own -- the rule decides -- so reading only the
 * territory's owners would show "no owner assigned" for a territory that routes perfectly well.
 * It asks the rules too, and names them.
 */
export const TerritorySummaryBubble: FC<Props> = ({
  map,
  territory,
  lngLat,
  alsoIn,
  onClose,
  onViewReport,
}) => {
  const dispatch = useAppDispatch()

  const boundaries = useSelector(selectTerritoryBoundariesById)
  const snapshots = useSelector(selectTerritorySnapshotsById)
  const loadingIds = useSelector(selectTerritorySnapshotLoadingIds)
  const errorIds = useSelector(selectTerritorySnapshotErrorIds)

  const boundary = boundaries[territory.id]
  const snapshot = snapshots[territory.id]
  const loading = loadingIds.includes(territory.id)
  const failed = errorIds.includes(territory.id)

  const [ruleUsage, setRuleUsage] = useState<TerritoryRuleUsage[] | null>(null)

  // The territory's OWN team and owners come from the TERRITORY, not from the preview snapshot.
  // /Territory/preview answers a question about a shape -- no id, no owner rows, no team -- so
  // it populates neither salesTeamName nor isTerritoryOwner, and reading them from it showed
  // "No team" on every saved territory and "No owner assigned" on any territory whose owners had
  // no routing rule. The snapshot still supplies the activity figures; it just cannot say who
  // the patch belongs to.
  //
  // Both names are resolved server-side, in the summaries payload. Looking them up in the
  // sales-team store would not work here: getSalesTeamList and getSalesTeamsUsers are dispatched
  // only by Settings and the pending-lead review, so on the map those stores are empty and every
  // lookup would miss -- reporting exactly the "No team" this is meant to fix.
  // Defaulted because this comes off the wire. The DTO always sends an array, but a summary
  // cached by an older client build would not, and `.length` on undefined takes the whole map
  // down rather than just the label.
  const ownerNames = territory.ownerNames ?? []

  const { height, measureRef } = useMeasuredTooltipHeight(
    BUBBLE_FALLBACK_HEIGHT,
  )

  const { left, top, transform, isOutOfViewport } = useMapLibreTooltipPosition({
    map,
    latitude: lngLat.lat,
    longitude: lngLat.lng,
    tooltipWidth: BUBBLE_WIDTH,
    tooltipHeight: height,
  })

  // The geometry is what the request is made of, and it is normally already cached -- the user
  // just clicked a polygon that is on screen, and only fetched geometry is drawn. Requested
  // here anyway for the case where a style reload or a cache eviction has dropped it, so the
  // bubble resolves rather than sitting empty.
  useEffect(() => {
    if (!boundary) dispatch(fetchTerritoryBoundaries([territory.id]))
  }, [dispatch, boundary, territory.id])

  // Which rules route through this territory. Not cached in the slice: rules are edited
  // elsewhere, and a stale answer here would describe routing that has since changed -- the one
  // thing this line exists to get right.
  useEffect(() => {
    let cancelled = false

    const run = async () => {
      const result = await dispatch(fetchTerritoryRuleUsage(territory.id))

      if (cancelled) return

      // Soft-fail to "no rules": the territory's own owners are still shown below, and a failed
      // lookup must not take the whole bubble down.
      setRuleUsage(
        fetchTerritoryRuleUsage.fulfilled.match(result) ? result.payload : [],
      )
    }

    run()
    return () => {
      cancelled = true
    }
  }, [dispatch, territory.id])

  // Fetched once per territory and cached in the slice; a repeat click reads it straight back.
  // A previous failure is NOT retried automatically -- that would retry on every re-render of a
  // bubble that is already open. The user retries with the link.
  useEffect(() => {
    if (!boundary || snapshot || loading || failed) return
    dispatch(fetchTerritorySnapshot({ id: territory.id, boundary }))
  }, [dispatch, boundary, snapshot, loading, failed, territory.id])

  if (isOutOfViewport) return null

  // Only enabled rules route anything, so only they get to answer "who gets leads here".
  const routingRules = (ruleUsage ?? []).filter((rule) => rule.isEnabled)

  /** Who a rule sends leads to: its owners, or its teams when it names no owners. */
  const routedTo = (rule: TerritoryRuleUsage) =>
    rule.ownerNames.length > 0
      ? rule.ownerNames.join(', ')
      : rule.salesTeamNames.length > 0
        ? `anyone on ${rule.salesTeamNames.join(', ')}`
        : 'nobody yet'

  return (
    <Paper
      ref={measureRef}
      elevation={6}
      data-test-id="territory-summary-bubble"
      sx={{
        position: 'absolute',
        left,
        top,
        transform,
        width: BUBBLE_WIDTH,
        p: 1.5,
        borderRadius: 2,
        pointerEvents: 'auto',
        zIndex: 1000,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box
          sx={{
            width: 12,
            height: 12,
            borderRadius: '2px',
            flexShrink: 0,
            bgcolor: territory.colorHex ?? DEFAULT_TERRITORY_COLOR,
          }}
        />
        <Typography variant="subtitle2" noWrap title={territory.name}>
          {territory.name}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close"
          sx={{ ml: 'auto' }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Typography variant="caption" color="text.secondary" display="block">
        {snapshot ? (
          <>
            {territory.salesTeamName ?? 'No team'}
            {ownerNames.length > 0 && ` · ${ownerNames.join(', ')}`}
            {/* No owners AND no rule: genuinely unassigned, and worth saying so plainly. */}
            {ownerNames.length === 0 &&
              routingRules.length === 0 &&
              ruleUsage !== null &&
              ' · No owner assigned'}
          </>
        ) : (
          // Falls back to the count the summary already carries, so a territory with owners
          // does not jump from empty to populated once the request lands. One with none stays
          // blank instead of flashing "No owners" and then correcting itself -- whether anybody
          // is assigned here is exactly what the request is still finding out.
          ownerCountLabel(territory.ownerIds.length)
        )}
      </Typography>

      {routingRules.length > 0 && (
        // The routing a territory drawn from a rule actually has. Named, not counted: an admin
        // reading this wants to know WHICH rule to open, and a rep wants to know whose patch
        // they are standing in.
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          data-test-id="territory-bubble-routing"
        >
          {routingRules.length === 1
            ? `Leads here go to ${routedTo(routingRules[0])} — rule “${routingRules[0].name}”`
            : `Leads here are routed by ${routingRules.length} rules: ${routingRules
                .map((rule) => `“${rule.name}” → ${routedTo(rule)}`)
                .join('; ')}`}
        </Typography>
      )}

      {failed ? (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Could not load this territory&apos;s numbers.{' '}
            <Link
              component="button"
              variant="body2"
              onClick={() =>
                boundary &&
                dispatch(
                  fetchTerritorySnapshot({
                    id: territory.id,
                    boundary,
                  }),
                )
              }
            >
              Try again
            </Link>
          </Typography>
        </Box>
      ) : (
        <>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1,
              mt: 1,
            }}
          >
            {METRICS.map((metric) => (
              <Box key={metric.label}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {metric.label}
                </Typography>
                {snapshot ? (
                  <Typography variant="body2" fontWeight={600}>
                    {metric.format(metric.value(snapshot))}
                  </Typography>
                ) : (
                  <Skeleton width={40} height={20} />
                )}
              </Box>
            ))}
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ mt: 1 }}
          >
            Contacts and size are current; activity covers the last 12 months.
          </Typography>
        </>
      )}

      {alsoIn.length > 0 && (
        // Overlapping territories are legal and the server counts a record in both, so saying
        // which others cover this spot is the difference between a surprising number and an
        // explained one.
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mt: 0.5 }}
        >
          Also inside: {alsoIn.map((t) => t.name).join(', ')}
        </Typography>
      )}

      {onViewReport && (
        <Button size="small" onClick={onViewReport} sx={{ mt: 1, px: 0 }}>
          View full report
        </Button>
      )}
    </Paper>
  )
}
